import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, PencilSimple, Trash, UsersThree } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const CURRENCIES = ["CAD", "USD", "RMB"];
const EMPTY_FORM = { name: "", currency: "CAD", coefficient: "1", price_per_skid: "0", sales_user_id: "" };
const UNASSIGNED_VALUE = "__unassigned__";

export default function Customers() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: custs }, salesRes] = await Promise.all([
        api.get("/customers"),
        // /users is admin/office only; sales/other roles ignore the failure and just skip
        // the Sales dropdown/column (which they shouldn't see anyway).
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setCustomers(custs);
      setSalesUsers((salesRes.data || []).filter((u) => u.role === "sales"));
    } catch (e) {
      toast({
        title: "Failed to load customers",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditTarget(c);
    setForm({
      name: c.name,
      currency: c.currency,
      coefficient: String(c.coefficient),
      price_per_skid: String(c.price_per_skid ?? 0),
      sales_user_id: c.sales_user_id || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      currency: form.currency,
      coefficient: parseFloat(form.coefficient) || 1,
      price_per_skid: parseFloat(form.price_per_skid) || 0,
      sales_user_id: form.sales_user_id || null,
    };
    if (!payload.name) return;
    setSaving(true);
    try {
      if (editTarget) {
        await api.patch(`/customers/${editTarget.id}`, payload);
        toast({ title: "Customer updated", description: "Linked quotes were re-priced automatically." });
      } else {
        await api.post("/customers", payload);
        toast({ title: "Customer created" });
      }
      setDialogOpen(false);
      fetchCustomers();
    } catch (e) {
      toast({
        title: "Save failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/customers/${deleteTarget.id}`);
      toast({ title: "Customer deleted" });
      setDeleteTarget(null);
      fetchCustomers();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="flex h-screen w-screen flex-col overflow-x-hidden bg-white" data-testid="customers-page">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-300 px-4 py-3 lg:px-6">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm border-zinc-300"
            onClick={() => navigate("/")}
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft size={14} className="mr-1.5" /> Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
              <UsersThree size={16} weight="bold" className="text-blue-600" />
            </div>
            <h1 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Customers</h1>
          </div>
        </div>
        <Button
          size="sm"
          className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          onClick={openCreate}
          data-testid="new-customer-button"
        >
          <Plus size={14} className="mr-1.5" /> New Customer
        </Button>
      </header>

      <div className="min-w-0 flex-1 overflow-auto p-4 lg:p-6">
        {loading ? (
          <div className="text-sm text-zinc-500">Loading...</div>
        ) : customers.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-zinc-400" data-testid="customers-empty-state">
            <UsersThree size={32} />
            <p className="text-sm">No customers yet. Click "New Customer" to add one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-zinc-300" data-testid="customers-table">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-zinc-50">
                <tr className="border-b border-zinc-300">
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Customer Name</th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Currency</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Price Coefficient</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Price/Skids</th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Sales</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-200 last:border-0" data-testid={`customer-row-${c.id}`}>
                    <td className="px-3 py-2 font-medium text-zinc-900">{c.name}</td>
                    <td className="px-3 py-2">
                      <span className="rounded-sm border border-zinc-300 bg-zinc-50 px-1.5 py-0.5 text-xs font-semibold text-zinc-700">
                        {c.currency}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data">{c.coefficient}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{(c.price_per_skid ?? 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-zinc-700" data-testid={`customer-sales-cell-${c.id}`}>
                      {c.sales_user_name ? (
                        <span className="text-sm">{c.sales_user_name}</span>
                      ) : (
                        <span className="text-xs italic text-zinc-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm border-zinc-300"
                          onClick={() => openEdit(c)}
                          data-testid={`edit-customer-button-${c.id}`}
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTarget(c)}
                          data-testid={`delete-customer-button-${c.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editTarget ? "Edit Customer" : "New Customer"}</DialogTitle>
            <DialogDescription>
              The price coefficient and inland freight (Price/Skids) are applied to all quotes linked to
              this customer: unit price = (price + Price/Skids × carton CBM ÷ 1.7 ÷ pcs per carton) × coefficient.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Customer Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="rounded-sm border-zinc-300"
                required
                data-testid="customer-name-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((cur) => (
                    <SelectItem key={cur} value={cur}>
                      {cur}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Price Coefficient</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={form.coefficient}
                onChange={(e) => setForm((f) => ({ ...f, coefficient: e.target.value }))}
                className="rounded-sm border-zinc-300"
                required
                data-testid="customer-coefficient-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Price/Skids (Inland Freight)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.price_per_skid}
                onChange={(e) => setForm((f) => ({ ...f, price_per_skid: e.target.value }))}
                className="rounded-sm border-zinc-300"
                data-testid="customer-price-per-skid-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Sales</Label>
              <Select
                value={form.sales_user_id || UNASSIGNED_VALUE}
                onValueChange={(v) => setForm((f) => ({ ...f, sales_user_id: v === UNASSIGNED_VALUE ? "" : v }))}
              >
                <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-sales-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_VALUE} data-testid="customer-sales-option-unassigned">
                    <span className="italic text-zinc-500">Unassigned</span>
                  </SelectItem>
                  {salesUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`customer-sales-option-${u.id}`}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                data-testid="customer-save-button"
              >
                {saving ? "Saving..." : editTarget ? "Save Changes" : "Create Customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Quotes linked to this customer will be unlinked but keep their current prices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
              data-testid="confirm-delete-customer-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
